import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Users, TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react'
import { api, messaggioErrore } from '../../lib/api'
import { Table, type Colonna } from '../../components/ui/Table'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Toggle } from '../../components/ui/Toggle'
import { Avatar } from '../../components/ui/Avatar'
import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../stores/toastStore'
import { formatDataOra } from '../../lib/format'
import pagina from '../pagine.module.css'

interface RigaTeam {
  utente: { id: string; nome: string; cognome: string; email: string }
  membro_id: string
  telefono: string
  attivo: boolean
  sessioni_totali: number
  sessioni_mese: number
  score_medio_apertura: number
  score_medio_obiezioni: number
  score_medio_chiusura: number
  voto_medio_complessivo: number
  ultima_sessione: string | null
  trend_30_giorni: 'su' | 'giù' | 'stabile'
}

interface InvitoInSospeso {
  id: string
  utente: { id: string; nome: string | null; cognome: string | null; email: string }
  messaggio: string | null
  created_at: string
  expires_at: string
}

interface RispostaTeam {
  membri: RigaTeam[]
  inviti_in_attesa: InvitoInSospeso[]
}

export function ManagerTeam() {
  const queryClient = useQueryClient()
  const [modaleAperto, setModaleAperto] = useState(false)

  const q = useQuery({
    queryKey: ['manager', 'team'],
    queryFn: async () =>
      (await api.get<{ dati: RispostaTeam }>('/manager/team')).data.dati!,
  })

  const toggle = useMutation({
    mutationFn: async (id: string) => api.put(`/manager/team/membri/${id}/toggle`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['manager', 'team'] }),
  })

  if (q.isLoading) return <div className={pagina.skeleton}>Caricamento…</div>
  if (q.error || !q.data) return <EmptyState titolo="Errore caricamento team" />

  const colonne: Colonna<RigaTeam & { id: string }>[] = [
    {
      chiave: 'nome',
      intestazione: 'Membro',
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar nome={r.utente.nome} cognome={r.utente.cognome} taglia="sm" />
          <div>
            <div style={{ fontWeight: 600 }}>{r.utente.nome} {r.utente.cognome}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.utente.email}</div>
          </div>
        </div>
      ),
    },
    {
      chiave: 'tel',
      intestazione: 'Telefono',
      render: (r) => <span className="mono" style={{ fontSize: 13 }}>{r.telefono}</span>,
      larghezza: '150px',
    },
    {
      chiave: 'mese',
      intestazione: 'Sess. mese',
      allineamento: 'right',
      render: (r) => <span className="mono">{r.sessioni_mese}</span>,
      larghezza: '110px',
    },
    {
      chiave: 'voto',
      intestazione: 'Voto medio',
      allineamento: 'right',
      render: (r) => (
        <span className="mono" style={{ fontWeight: 600 }}>
          {r.voto_medio_complessivo > 0 ? `${r.voto_medio_complessivo}/10` : '—'}
        </span>
      ),
      larghezza: '110px',
    },
    {
      chiave: 'trend',
      intestazione: 'Trend',
      render: (r) => (
        <Badge variante={r.trend_30_giorni === 'su' ? 'success' : r.trend_30_giorni === 'giù' ? 'danger' : 'default'}>
          {r.trend_30_giorni === 'su' && <TrendingUp size={11} />}
          {r.trend_30_giorni === 'giù' && <TrendingDown size={11} />}
          {r.trend_30_giorni === 'stabile' && <Minus size={11} />}
          {' '}{r.trend_30_giorni}
        </Badge>
      ),
      larghezza: '110px',
    },
    {
      chiave: 'ultima',
      intestazione: 'Ultima sessione',
      render: (r) => (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {r.ultima_sessione ? formatDataOra(r.ultima_sessione) : '—'}
        </span>
      ),
      larghezza: '170px',
    },
    {
      chiave: 'attivo',
      intestazione: 'Attivo',
      render: (r) => (
        <Toggle attivo={r.attivo} onChange={() => toggle.mutate(r.membro_id)} />
      ),
      larghezza: '90px',
    },
  ]

  // Aggiungi un campo `id` univoco per la chiave React
  const righe = q.data.membri.map((r) => ({ ...r, id: r.membro_id }))
  const inviti = q.data.inviti_in_attesa ?? []

  return (
    <div className={pagina.wrap}>
      <div className={pagina.header}>
        <div>
          <h1 className={pagina.titolo}>Membri del team</h1>
          <div className={pagina.sottotitolo}>
            {righe.length} membri totali — usa il toggle per sospendere temporaneamente un accesso.
          </div>
        </div>
        <Button iconaSinistra={<Plus size={16} />} onClick={() => setModaleAperto(true)}>
          Aggiungi membro
        </Button>
      </div>

      <Table
        colonne={colonne}
        righe={righe}
        vuoto={
          <EmptyState
            icona={<Users size={40} />}
            titolo="Nessun membro nel team"
            descrizione="Aggiungi il primo membro del tuo team per iniziare."
            azione={<Button onClick={() => setModaleAperto(true)}>Aggiungi</Button>}
          />
        }
      />

      {inviti.length > 0 && (
        <Card titolo={`Inviti in sospeso (${inviti.length})`}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {inviti.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '12px 14px',
                  background: 'var(--bg-subtle)',
                  border: '1px dashed var(--border)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <Avatar nome={inv.utente.nome ?? '?'} cognome={inv.utente.cognome ?? ''} taglia="sm" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {inv.utente.nome} {inv.utente.cognome}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {inv.utente.email}
                  </div>
                  {inv.messaggio && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontStyle: 'italic' }}>
                      "{inv.messaggio}"
                    </div>
                  )}
                </div>
                <Badge variante="warning">
                  <Clock size={11} /> In attesa
                </Badge>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  Scade {new Date(inv.expires_at).toLocaleDateString('it-IT')}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <ModaleAggiungiMembro
        aperto={modaleAperto}
        onChiudi={() => setModaleAperto(false)}
        onAggiunto={() => {
          setModaleAperto(false)
          queryClient.invalidateQueries({ queryKey: ['manager', 'team'] })
        }}
      />
    </div>
  )
}

interface RispostaAggiungi {
  tipo: 'invito_creato' | 'membro_aggiunto'
  messaggio: string
}

function ModaleAggiungiMembro({ aperto, onChiudi, onAggiunto }:
  { aperto: boolean; onChiudi: () => void; onAggiunto: () => void }) {
  const toast = useToast()
  const [nome, setNome] = useState('')
  const [cognome, setCognome] = useState('')
  const [email, setEmail] = useState('')
  const [telefono, setTelefono] = useState('')
  const [messaggio, setMessaggio] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [caricamento, setCaricamento] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErrore(null)
    setCaricamento(true)
    try {
      const res = await api.post<{ dati: RispostaAggiungi }>('/manager/team/membri', {
        nome, cognome, email, telefono,
        ...(messaggio.trim() ? { messaggio: messaggio.trim() } : {}),
      })
      // Il backend ritorna `tipo` diverso a seconda del flusso (invito
      // creato vs membro aggiunto direttamente). Usiamo il messaggio
      // restituito per dare feedback contestualizzato all'utente.
      const dati = res.data.dati
      if (dati?.messaggio) {
        toast.successo(dati.messaggio)
      } else {
        toast.successo('Membro aggiunto.')
      }
      setNome(''); setCognome(''); setEmail(''); setTelefono(''); setMessaggio('')
      onAggiunto()
    } catch (err) {
      setErrore(messaggioErrore(err))
    } finally {
      setCaricamento(false)
    }
  }

  return (
    <Modal aperto={aperto} titolo="Aggiungi membro al team" onChiudi={onChiudi}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Campo label="Nome" valore={nome} onChange={setNome} />
        <Campo label="Cognome" valore={cognome} onChange={setCognome} />
        <Campo label="Email" valore={email} onChange={setEmail} type="email" />
        <Campo label="Telefono (E.164)" valore={telefono} onChange={setTelefono} placeholder="+393471234567" />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
            Messaggio per l'SDR (opzionale)
          </label>
          <textarea
            value={messaggio}
            onChange={(e) => setMessaggio(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Visibile nella modale di invito se l'SDR è già registrato."
            className={pagina.input}
            style={{ height: 'auto', padding: 10, fontFamily: 'var(--font-sans)' }}
          />
        </div>

        {errore && (
          <div style={{ padding: 10, background: 'var(--danger-soft)', color: 'var(--danger)', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            {errore}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variante="ghost" onClick={onChiudi}>Annulla</Button>
          <Button type="submit" caricamento={caricamento}>Aggiungi e invita</Button>
        </div>
      </form>
    </Modal>
  )
}

function Campo({ label, valore, onChange, type, placeholder }:
  { label: string; valore: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{label}</label>
      <input
        type={type ?? 'text'}
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={pagina.input}
        style={{ height: 40 }}
        required
      />
    </div>
  )
}
